import { DATE_PIPE_DEFAULT_OPTIONS, DatePipe, DecimalPipe, registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localePl from '@angular/common/locales/pl';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { MAT_CARD_CONFIG } from '@angular/material/card';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_LUXON_DATE_ADAPTER_OPTIONS, provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { provideRouter, Router, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { progressInterceptor } from 'ngx-progressbar/http';
import { provideNgProgressRouter } from 'ngx-progressbar/router';
import { firstValueFrom } from 'rxjs';

import { UserDetailsStorageService } from '@app/security/data-access/user/user-details-storage.service';
import { UserService } from '@app/security/data-access/user/user.service';
import { HankoAuthService } from '@app/security/feature/hanko/data-access/hanko-auth.service';
import { hankoCredentialsInterceptor } from '@app/security/feature/hanko/data-access/hanko-credentials.interceptor';

import { errorNotificationInterceptor } from '@shared/data-access/error-notification.interceptor';
import { provideIcons } from '@shared/data-access/icons/icons.provider';
import { CustomErrorHandler } from '@shared/features/error-handling';

import { environment } from '@environments/environment';

import { routes } from './app.routes';

registerLocaleData(localePl);

const MAT_DATE_FORMATS_PL = {
  parse: {
    dateInput: 'dd.MM.yyyy',
    timeInput: 'HH:mm',
  },
  display: {
    dateInput: 'dd.MM.yyyy',
    timeInput: 'HH:mm',
    timeOptionLabel: 'HH:mm',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'dd.MM.yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' })
    ),

    { provide: LOCALE_ID, useValue: 'pl-PL' },

    provideHttpClient(
      withFetch(),
      withInterceptors([hankoCredentialsInterceptor, progressInterceptor, errorNotificationInterceptor])
    ),

    provideNgProgressRouter({}),
    provideIcons(),
    provideEnvironmentNgxMask(),

    DatePipe,
    DecimalPipe,

    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'fill', floatLabel: 'always', subscriptSizing: 'dynamic' },
    },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { autoFocus: false, restoreFocus: true } },
    { provide: MAT_CARD_CONFIG, useValue: { appearance: 'outlined' } },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: 'dd.MM.yyyy' } },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FORMATS_PL },

    provideLuxonDateAdapter(MAT_DATE_FORMATS_PL),
    {
      provide: MAT_LUXON_DATE_ADAPTER_OPTIONS,
      useValue: { useUtc: true, firstDayOfWeek: 1, defaultOutputCalendar: 'UTC' },
    },

    { provide: ErrorHandler, useClass: CustomErrorHandler },
    ...(environment.sentry
      ? [
          { provide: Sentry.TraceService, deps: [Router] },
          provideAppInitializer(() => {
            inject(Sentry.TraceService);
          }),
        ]
      : []),

    provideAppInitializer(async () => {
      const hankoAuthService = inject(HankoAuthService);
      const userService = inject(UserService);
      const userDetailsStorage = inject(UserDetailsStorageService);

      const isAuthenticated = await hankoAuthService.revalidateSession();
      if (isAuthenticated && !userDetailsStorage.user()) {
        await firstValueFrom(userService.initializeUser());
      }
    }),
  ],
};
